import {
  CheckCircle,
  CheckCircleOutline,
  DoNotDisturb,
  RadioButtonUnchecked,
} from "@mui/icons-material";
import { Divider, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import {
  Exercise,
  PlanWeek,
  PlanWorkout,
  WorkoutCategories,
  WorkoutStatusEnum,
} from "@readyfit-common/models";
import moment from "moment";
import { EditIcon, TrashIcon } from "src/grading/assets";
import { useAppDispatch } from "src/store/hooks";
import {
  setDeleteWorkoutModalOpen,
  setSelectedDate,
  setSelectedDayNumber,
  setSelectedWeek,
  setSelectedWorkout,
  setWorkoutModalOpen,
} from "src/store/reducers/training/trainingApiSlice";
import useStyles from "./DayCard.styles";
import Header from "./Header/Header";

const RenderWorkoutStatusCheck = (workoutStatusId: string) => {
  if (workoutStatusId === WorkoutStatusEnum.didIt) {
    return <CheckCircle sx={{ color: "#27AE60" }} />;
  }

  if (workoutStatusId === WorkoutStatusEnum.noResults) {
    return <RadioButtonUnchecked />;
  }

  if (workoutStatusId === WorkoutStatusEnum.didNotDoIt) {
    return <DoNotDisturb style={{ color: "#EB5757" }} />;
  }

  if (workoutStatusId === WorkoutStatusEnum.partiallyDone) {
    return <CheckCircleOutline />;
  }

  return null;
};

const RenderActionButtons = (
  workout: PlanWorkout,
  index: number,
  week: PlanWeek,
  date: string,
) => {
  const dispatch = useAppDispatch();
  const handleOpenWorkoutModal = (workout: PlanWorkout) => {
    dispatch(setWorkoutModalOpen(true));
    dispatch(setSelectedWorkout(workout));
    dispatch(setSelectedDayNumber(index + 1));
    dispatch(setSelectedWeek(week));
    dispatch(setSelectedDate(date));
  };

  const handleOpenDeleteWorkoutModal = (workout: PlanWorkout) => {
    dispatch(setDeleteWorkoutModalOpen(true));
    dispatch(setSelectedWorkout(workout));
    dispatch(setSelectedWeek(week));
  };

  const isSameDayOrBefore = moment(date).isBefore(moment(), "day");

  return isSameDayOrBefore ? null : (
    <Grid container flexDirection={"row"}>
      <Grid item xs={6}>
        <Tooltip title="Edit">
          <IconButton onClick={() => handleOpenWorkoutModal(workout)}>
            <EditIcon stroke="#000" />
          </IconButton>
        </Tooltip>
      </Grid>
      <Grid item xs={6}>
        <Tooltip title="Remove">
          <IconButton onClick={() => handleOpenDeleteWorkoutModal(workout)}>
            <TrashIcon stroke="#000" />
          </IconButton>
        </Tooltip>
      </Grid>
    </Grid>
  );
};

interface DayCardProps {
  workouts: PlanWorkout[];
  week: PlanWeek;
  isToday?: boolean;
  isEditable?: boolean;
  isTemplate?: boolean;
  index: number;
  date: string;
}

const WorkoutCategoryDetailSection = ({
  label,
  completed,
  total,
  units,
  isTemplate,
}: {
  label: string;
  completed: string;
  total?: string;
  units?: string;
  isTemplate?: boolean;
}) => {
  const completedValue =
    isTemplate && total
      ? total
      : total && completed
        ? `${completed}/${total}`
        : completed;
  return (
    <Grid container flexDirection={"column"}>
      <Typography variant="subtitle1" color={"#8F8F8F"} fontWeight={"bold"}>
        {label.toUpperCase()}
      </Typography>
      <Typography variant="subtitle1" color={"#000"}>
        {completedValue} {units ? units : ""}
      </Typography>
    </Grid>
  );
};

const FitnessWorkoutCategoryDetailSection = ({
  exercise,
}: {
  exercise: Exercise;
}) => {
  return (
    <Grid container flexDirection={"column"}>
      <Typography variant="subtitle1" color={"#8F8F8F"} fontWeight={"bold"}>
        {exercise.name}
      </Typography>
      <Typography variant="subtitle1" color={"#000"}>
        {exercise.completedSets || 0}/{exercise.sets || 0} sets
      </Typography>
      <Typography variant="subtitle1" color={"#000"}>
        {exercise.completedReps || 0}/{exercise.reps || 0} reps
      </Typography>
    </Grid>
  );
};

const DayCard = ({
  workouts = [],
  week,
  isToday,
  isEditable,
  isTemplate,
  index,
  date,
}: DayCardProps) => {
  const css = useStyles();

  const displayWorkoutCategoryDetails = (workout: PlanWorkout) => {
    if (!workout.workoutCategory) {
      return null;
    }

    if (workout.workoutCategoryId === WorkoutCategories.FITNESS) {
      return workout.exercises?.map((exercise) => {
        return (
          <FitnessWorkoutCategoryDetailSection
            exercise={exercise}
            key={exercise.id}
          />
        );
      });
    }

    if (workout.workoutCategoryId === WorkoutCategories.RUN) {
      return (
        <Grid container rowGap={1.5}>
          <WorkoutCategoryDetailSection
            label={"Distance"}
            completed={workout.completedDistance?.toString() || "0"}
            total={workout.distance?.toString() || "0"}
            units={"mi"}
            isTemplate={isTemplate}
          />

          {!isTemplate && (
            <WorkoutCategoryDetailSection
              label={"Average Pace"}
              completed={workout.averagePace?.toString() || "0"}
              units={"mi/min"}
              isTemplate={isTemplate}
            />
          )}
        </Grid>
      );
    }

    if (workout.workoutCategoryId === WorkoutCategories.SWIM) {
      return (
        <Grid container rowGap={1.5}>
          <WorkoutCategoryDetailSection
            label={"Distance"}
            completed={workout.completedDistance?.toString() || "0"}
            total={workout.distance?.toString() || "0"}
            units={"yards"}
          />

          {!isTemplate && (
            <WorkoutCategoryDetailSection
              label={"Average Pace"}
              completed={workout.averagePace?.toString() || "0"}
              units={"yards/min"}
            />
          )}
        </Grid>
      );
    }

    return (
      <Typography variant="subtitle1" color={"black"} fontWeight={"bold"}>
        {workout?.workoutCategory?.name}
      </Typography>
    );
  };

  return (
    <div className={`${css.wrapperCard}`}>
      <Header
        workouts={workouts}
        week={week}
        isToday={isToday}
        isTemplate={isTemplate}
        isEditable={isEditable}
        index={index}
        date={date}
      />

      <Grid container className={`${css.body} ${isToday ? css.todayBody : ""}`}>
        <Grid item className={css.bodyContent}>
          {workouts?.map((workout, i) => (
            <>
              <Grid
                key={workout.id}
                className={css.bodyContentRow}
                // style={
                //   i !== workouts.length - 1
                //     ? {
                //         borderBottom: "1px solid #e8e8e8",
                //       }
                //     : {}
                // }
              >
                <Grid container>
                  <Grid item xs={6} alignItems={"center"}>
                    <Typography
                      variant="subtitle1"
                      color={"black"}
                      fontWeight={"bold"}
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <span style={{ marginRight: "8px" }}>&bull;</span>
                      {workout?.workoutCategory?.name}
                    </Typography>

                    <Typography
                      variant="subtitle1"
                      color={"black"}
                      fontWeight={"bold"}
                      fontSize={14}
                    >
                      {workout.name}
                    </Typography>
                  </Grid>

                  <Grid container item xs={6} justifyContent={"flex-end"}>
                    {isEditable
                      ? RenderActionButtons(workout, index, week, date)
                      : RenderWorkoutStatusCheck(workout.workoutStatusId)}
                  </Grid>
                </Grid>

                {displayWorkoutCategoryDetails(workout)}
              </Grid>
              {i !== workouts.length - 1 && (
                <Divider sx={{ marginTop: "12px", marginBottom: "12px" }} />
              )}
            </>
          ))}
        </Grid>
      </Grid>
    </div>
  );
};

export default DayCard;
